/**
 * useContentLanguage — reads the reader's current "translate-to"
 * language (from the googtrans cookie set by <TranslateWidget />) and
 * exposes it as a hook. If no translation is active, defaults to
 * ``"en"``.
 *
 * Compose forms use this to pre-select the "You're writing in …"
 * dropdown so a user browsing in Malayalam who taps "Share a story"
 * gets Malayalam pre-selected — matching the intuition that content
 * you consume in a language, you also create in that language.
 */
import { useEffect, useState } from "react";

const COOKIE_NAME = "googtrans";

// Curated language table — keep in sync with TranslateWidget.jsx.
export const CONTENT_LANGUAGES = [
  { code: "en",    label: "English" },
  { code: "es",    label: "Español" },
  { code: "hi",    label: "हिन्दी" },
  { code: "ta",    label: "தமிழ்" },
  { code: "ml",    label: "മലയാളം" },
  { code: "te",    label: "తెలుగు" },
  { code: "kn",    label: "ಕನ್ನಡ" },
  { code: "mr",    label: "मराठी" },
  { code: "bn",    label: "বাংলা" },
  { code: "gu",    label: "ગુજરાતી" },
  { code: "pa",    label: "ਪੰਜਾਬੀ" },
  { code: "ur",    label: "اردو" },
  { code: "ar",    label: "العربية" },
  { code: "fr",    label: "Français" },
  { code: "de",    label: "Deutsch" },
  { code: "pt",    label: "Português" },
  { code: "ru",    label: "Русский" },
  { code: "zh-CN", label: "中文 (简体)" },
  { code: "ja",    label: "日本語" },
  { code: "ko",    label: "한국어" },
  { code: "it",    label: "Italiano" },
  { code: "nl",    label: "Nederlands" },
  { code: "tr",    label: "Türkçe" },
  { code: "vi",    label: "Tiếng Việt" },
  { code: "th",    label: "ภาษาไทย" },
  { code: "id",    label: "Bahasa Indonesia" },
];

export function labelForLanguage(code) {
  if (!code) return null;
  return CONTENT_LANGUAGES.find((l) => l.code === code)?.label
      || CONTENT_LANGUAGES.find((l) => l.code.split("-")[0] === code)?.label
      || code.toUpperCase();
}

function readCookie() {
  if (typeof document === "undefined") return "en";
  const raw = document.cookie || "";
  const match = raw.split("; ").find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return "en";
  const parts = match.split("=")[1].split("/");
  return parts[2] || "en";
}

export default function useContentLanguage() {
  const [code, setCode] = useState(() => readCookie());
  useEffect(() => {
    const t = setInterval(() => setCode(readCookie()), 2000);
    return () => clearInterval(t);
  }, []);
  return code;
}
